import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  GitBranch,
  HardDrive,
  Loader2,
  PlayCircle,
  ServerCrash,
  X,
  XCircle,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  type DatalakeExecution,
  type DatalakeRunStatus,
  type DatalakeStateSpan,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import {
  formatBytes,
  formatDuration,
  formatNumber,
  formatRelative,
} from '../lib/format';
import { cn } from '../lib/utils';

const STATUS_LABEL: Record<DatalakeRunStatus, string> = {
  success: 'Succeeded',
  partial: 'Partial',
  failed: 'Failed',
  'no-work': 'No work',
  unknown: 'Unknown',
};

const STATUS_VARIANT: Record<
  DatalakeRunStatus,
  'success' | 'warning' | 'destructive' | 'secondary' | 'outline'
> = {
  success: 'success',
  partial: 'warning',
  failed: 'destructive',
  'no-work': 'secondary',
  unknown: 'outline',
};

export function DatalakeStatus() {
  const runs = useQuery({
    queryKey: ['datalake', 'runs'],
    queryFn: () => api.listDatalakeRuns(50),
    staleTime: 5 * 60 * 1000,
  });

  const executions = useQuery({
    queryKey: ['datalake', 'executions'],
    queryFn: () => api.listDatalakeExecutions(),
    refetchInterval: 30 * 1000,
    staleTime: 15 * 1000,
  });

  const metrics = useQuery({
    queryKey: ['datalake', 'metrics', 14],
    queryFn: () => api.getDatalakeMetrics(14),
    staleTime: 60 * 1000,
  });

  const [selectedExecutionArn, setSelectedExecutionArn] = useState<string | null>(null);

  const items = runs.data?.items ?? [];
  const latest = items[0];
  const running = executions.data?.running ?? [];
  const recentExecutions = executions.data?.recent ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Datalake · Status</h1>
        <p className="text-muted-foreground">
          Is the daily extractor pipeline healthy? Stats and throughput from the run logs +
          Step Functions state.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Last run"
          icon={<Clock className="h-4 w-4 text-muted-foreground" />}
          loading={runs.isLoading}
        >
          {latest ? (
            <>
              <div className="flex items-center gap-2">
                <Badge variant={STATUS_VARIANT[latest.status]}>
                  {STATUS_LABEL[latest.status]}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatRelative(latest.lastModified)}
              </p>
            </>
          ) : (
            <span className="text-muted-foreground">No runs yet</span>
          )}
        </StatCard>

        <StatCard
          title="Currently running"
          icon={
            running.length > 0 ? (
              <Loader2 className="h-4 w-4 text-primary animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4 text-muted-foreground" />
            )
          }
          loading={executions.isLoading}
        >
          {running.length === 0 ? (
            <span className="text-muted-foreground">Idle</span>
          ) : (
            <>
              <div className="text-2xl font-bold">{running.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                started {formatDuration(running[0]?.startDate ?? null, null)} ago
              </p>
            </>
          )}
        </StatCard>

        <StatCard
          title="Dates succeeded (last run)"
          icon={<CheckCircle2 className="h-4 w-4 text-success" />}
          loading={runs.isLoading}
        >
          <div className="text-2xl font-bold">{formatNumber(latest?.succeeded)}</div>
          {latest?.failed != null && latest.failed > 0 && (
            <p className="text-xs text-destructive mt-1">
              {formatNumber(latest.failed)} failed
            </p>
          )}
        </StatCard>

        <StatCard
          title="Compression (last run)"
          icon={<HardDrive className="h-4 w-4 text-muted-foreground" />}
          loading={runs.isLoading}
        >
          <div className="text-2xl font-bold">{latest?.compressionRatio ?? '—'}</div>
          <p className="text-xs text-muted-foreground mt-1">
            {formatBytes(latest?.rawBytes)} → {formatBytes(latest?.parquetBytes)}
          </p>
        </StatCard>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">Throughput · last 14 days</CardTitle>
          <span className="text-xs text-muted-foreground">
            Dates pulled, dates failed, and servers the run could not reach
          </span>
        </CardHeader>
        <CardContent>
          <ThroughputChart
            data={buildChartData(metrics.data?.series ?? [])}
            loading={metrics.isLoading}
          />
        </CardContent>
      </Card>

      {latest?.emptyServers && latest.emptyServers.length > 0 && (
        <EmptyServersCard
          servers={latest.emptyServers}
          totalEmptyDates={latest.emptyDates}
          runTimestamp={latest.timestamp}
        />
      )}

      <ExecutionsCard
        running={running}
        recent={recentExecutions}
        loading={executions.isLoading}
        onSelect={(arn) => setSelectedExecutionArn(arn)}
      />

      {selectedExecutionArn && (
        <ExecutionDetailDrawer
          executionArn={selectedExecutionArn}
          onClose={() => setSelectedExecutionArn(null)}
        />
      )}
    </div>
  );
}

function StatCard({
  title,
  icon,
  children,
  loading,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>{loading ? <span className="text-muted-foreground">…</span> : children}</CardContent>
    </Card>
  );
}

interface ChartPoint {
  date: string;
  succeeded: number;
  failed: number;
  discoveryErrors: number;
}

/**
 * `DiscoveryErrors` is charted alongside the date counters because it is the one
 * failure they cannot show: a server whose archive index could not be read never
 * gets a single date queued, so it moves neither DatesSucceeded nor DatesFailed.
 * A morning where a customer pulled nothing would otherwise draw as a clean bar.
 */
const SERIES: { label: string; key: keyof Omit<ChartPoint, 'date'> }[] = [
  { label: 'DatesSucceeded', key: 'succeeded' },
  { label: 'DatesFailed', key: 'failed' },
  { label: 'DiscoveryErrors', key: 'discoveryErrors' },
];

function buildChartData(
  series: { label: string; timestamps: string[]; values: number[] }[],
): ChartPoint[] {
  const found = SERIES.map((s) => ({ ...s, data: series.find((x) => x.label === s.label) }));

  const dates = new Set<string>();
  for (const s of found) for (const ts of s.data?.timestamps ?? []) dates.add(ts);
  const sorted = Array.from(dates).sort();

  const points: ChartPoint[] = sorted.map((ts) => ({
    date: new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    succeeded: 0,
    failed: 0,
    discoveryErrors: 0,
  }));

  for (const s of found) {
    s.data?.timestamps.forEach((ts, i) => {
      const idx = sorted.indexOf(ts);
      const point = points[idx];
      if (idx >= 0 && point) point[s.key] = s.data?.values[i] ?? 0;
    });
  }
  return points;
}

const LEGEND: Record<string, string> = {
  succeeded: 'Dates succeeded',
  failed: 'Dates failed',
  discoveryErrors: 'Servers unreachable',
};

function ThroughputChart({ data, loading }: { data: ChartPoint[]; loading: boolean }) {
  if (loading) {
    return <div className="h-56 flex items-center justify-center text-muted-foreground">Loading chart…</div>;
  }
  if (data.length === 0) {
    return (
      <div className="h-56 flex items-center justify-center text-muted-foreground">
        No metric data in the last 14 days.
      </div>
    );
  }
  return (
    <div className="h-56">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }}
            tickLine={false}
            axisLine={false}
            width={50}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-muted)' }}
            contentStyle={{
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--color-foreground)',
            }}
          />
          <Legend
            iconType="square"
            wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
            formatter={(value: string) => LEGEND[value] ?? value}
          />
          <Bar dataKey="succeeded" stackId="a" fill="var(--color-success)" />
          <Bar dataKey="failed" stackId="a" fill="var(--color-destructive)" />
          <Bar
            dataKey="discoveryErrors"
            stackId="a"
            fill="var(--color-warn)"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyServersCard({
  servers,
  totalEmptyDates,
  runTimestamp,
}: {
  servers: { clientName: string; datesEmpty: number }[];
  totalEmptyDates: number | null;
  runTimestamp: string | null;
}) {
  const sorted = [...servers].sort((a, b) => b.datesEmpty - a.datesEmpty);
  return (
    <Card className="overflow-hidden border-warning/40">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <ServerCrash className="h-4 w-4 text-warning" />
          Servers with no sensor data
          <Badge variant="warning">{sorted.length}</Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Source servers that answered the API but returned no <code className="font-mono">groupDevices</code> or measurements
          for every date in the last run
          {runTimestamp ? <> ({formatRelative(runTimestamp)})</> : null}.
          {totalEmptyDates != null && totalEmptyDates > 0 && (
            <> {formatNumber(totalEmptyDates)} empty date{totalEmptyDates === 1 ? '' : 's'} total.</>
          )}
          {' '}Usually means the XiltriX server is misconfigured upstream - re-running the
          pipeline won't help until the device tree is restored on the source.
        </p>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-y border-border">
            <tr className="text-left text-muted-foreground">
              <th className="px-4 py-2 font-medium">Client</th>
              <th className="px-4 py-2 font-medium tabular-nums">Empty dates this run</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.clientName} className="border-b border-border last:border-0">
                <td className="px-4 py-2 font-mono text-xs">{s.clientName}</td>
                <td className="px-4 py-2 tabular-nums text-muted-foreground">{formatNumber(s.datesEmpty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ExecutionsCard({
  running,
  recent,
  loading,
  onSelect,
}: {
  running: DatalakeExecution[];
  recent: DatalakeExecution[];
  loading: boolean;
  onSelect: (arn: string) => void;
}) {
  const runningArns = new Set(running.map((e) => e.executionArn));
  const merged = [
    ...running,
    ...recent.filter((e) => !runningArns.has(e.executionArn)),
  ].slice(0, 20);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-muted-foreground" />
          State Machine executions
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Recent runs of <code className="font-mono">x5DataExtractorStateMachine</code>. Click any
          row to see the per-step timeline.
        </p>
      </CardHeader>
      {loading && (
        <div className="px-6 pb-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading executions…
        </div>
      )}
      {!loading && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-y border-border">
              <tr className="text-left text-muted-foreground">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">Duration</th>
              </tr>
            </thead>
            <tbody>
              {merged.map((e) => (
                <tr
                  key={e.executionArn}
                  onClick={() => onSelect(e.executionArn)}
                  className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  <td className="px-4 py-2 font-mono text-xs">{e.name}</td>
                  <td className="px-4 py-2">
                    <ExecutionStatusBadge status={e.status} />
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">
                    {e.startDate ? (
                      <span title={e.startDate}>{formatRelative(e.startDate)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2 text-muted-foreground tabular-nums">
                    {formatDuration(e.startDate, e.stopDate)}
                  </td>
                </tr>
              ))}
              {merged.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-6 text-center text-muted-foreground">
                    No executions in the recent history.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function ExecutionStatusBadge({ status }: { status: string }) {
  const variant =
    status === 'SUCCEEDED'
      ? 'success'
      : status === 'FAILED' || status === 'TIMED_OUT' || status === 'ABORTED'
        ? 'destructive'
        : status === 'RUNNING'
          ? 'default'
          : 'outline';
  return <Badge variant={variant}>{status}</Badge>;
}

function ExecutionDetailDrawer({
  executionArn,
  onClose,
}: {
  executionArn: string;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: ['datalake', 'execution', executionArn],
    queryFn: () => api.getDatalakeExecutionDetail(executionArn),
  });

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-3xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold truncate">{detail.data?.name ?? 'Execution'}</h2>
            <p className="text-xs text-muted-foreground font-mono truncate">{executionArn}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-5">
          {detail.isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading execution history…
            </div>
          )}
          {detail.error && <p className="text-destructive">{(detail.error as Error).message}</p>}
          {detail.data && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <ExecutionStatusBadge status={detail.data.status ?? 'UNKNOWN'} />
                {detail.data.truncated && (
                  <Badge variant="outline" className="text-[10px]">
                    history truncated at {detail.data.eventCount} events
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Tile
                  label="Started"
                  value={
                    detail.data.startDate ? (
                      <span title={detail.data.startDate} className="text-base">
                        {formatRelative(detail.data.startDate)}
                      </span>
                    ) : (
                      '—'
                    )
                  }
                  icon={<Clock className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="Duration"
                  value={
                    <span className="text-base">
                      {formatDuration(detail.data.startDate, detail.data.stopDate)}
                    </span>
                  }
                  icon={<Clock className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="History events"
                  value={detail.data.eventCount}
                  icon={<Database className="h-4 w-4 text-muted-foreground" />}
                />
                <Tile
                  label="Failures"
                  value={detail.data.failures.length}
                  icon={<XCircle className="h-4 w-4 text-destructive" />}
                  emphasize={detail.data.failures.length > 0}
                />
              </div>

              {detail.data.failures.length > 0 && (
                <section>
                  <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                    Failures ({detail.data.failures.length})
                  </h3>
                  <Card className="overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 border-b border-border">
                          <tr className="text-left text-muted-foreground">
                            <th className="px-4 py-2 font-medium">When</th>
                            <th className="px-4 py-2 font-medium">Type</th>
                            <th className="px-4 py-2 font-medium">Error</th>
                            <th className="px-4 py-2 font-medium">Cause</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.data.failures.map((f, i) => (
                            <tr key={i} className="border-b border-border last:border-0">
                              <td className="px-4 py-2 text-muted-foreground">
                                {f.timestamp ? formatRelative(f.timestamp) : '—'}
                              </td>
                              <td className="px-4 py-2 font-mono">{f.type ?? '—'}</td>
                              <td className="px-4 py-2 text-destructive">{f.error ?? '—'}</td>
                              <td className="px-4 py-2 text-muted-foreground max-w-md">
                                {f.cause ? (
                                  <pre className="font-mono whitespace-pre-wrap text-[11px]">
                                    {truncate(f.cause, 400)}
                                  </pre>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </section>
              )}

              <section>
                <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                  <GitBranch className="h-4 w-4 text-muted-foreground" />
                  State timeline ({detail.data.states.length})
                </h3>
                <Card className="overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr className="text-left text-muted-foreground">
                          <th className="px-4 py-2 font-medium">State</th>
                          <th className="px-4 py-2 font-medium">Entered</th>
                          <th className="px-4 py-2 font-medium">Exited</th>
                          <th className="px-4 py-2 font-medium">Duration</th>
                          <th className="px-4 py-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.data.states.map((s, i) => (
                          <StateRow key={`${s.name}-${i}`} span={s} />
                        ))}
                        {detail.data.states.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                              No state transitions in the history window.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StateRow({ span }: { span: DatalakeStateSpan }) {
  const variant =
    span.status === 'in-progress' ? 'default' : span.status === 'failed' ? 'destructive' : 'success';
  return (
    <tr className="border-b border-border last:border-0">
      <td className="px-4 py-2 font-mono">{span.name}</td>
      <td className="px-4 py-2 text-muted-foreground">
        {span.entered ? <span title={span.entered}>{formatRelative(span.entered)}</span> : '—'}
      </td>
      <td className="px-4 py-2 text-muted-foreground">
        {span.exited ? <span title={span.exited}>{formatRelative(span.exited)}</span> : '—'}
      </td>
      <td className="px-4 py-2 tabular-nums text-muted-foreground">
        {span.durationMs == null
          ? '—'
          : span.durationMs < 1000
            ? `${span.durationMs}ms`
            : `${(span.durationMs / 1000).toFixed(1)}s`}
      </td>
      <td className="px-4 py-2">
        <Badge variant={variant}>{span.status}</Badge>
      </td>
    </tr>
  );
}

function Tile({
  label,
  value,
  icon,
  emphasize,
}: {
  label: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  emphasize?: boolean;
}) {
  return (
    <Card className={cn(emphasize && 'border-destructive/40')}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold tabular-nums', emphasize && 'text-destructive')}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max) + '…';
}
